import React from "react";
import { InlineMath } from "react-katex";

import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import {
  pickCurveStyle,
  sampleAnchorCurve,
} from "./helpers/smoothGraph";

import {
  randInt,
  parseSingleInterval,
  parseIntervalsUnion,
  getIntervalRaw,
  intervalsEqualList,
} from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/helpers/MaturaPiecewiseFunctionUtils";

/* =============================================================
   TASK: ReadRangeFromGraphTask
   Cel: Odczytaj zbiór wartości funkcji z wykresu.

   Warianty:
   (A) ciągła łamana (2 odcinki) -> ZW = jeden przedział
   (B) rozłączna (2 odcinki z przerwą) -> ZW = przedział lub suma przedziałów

   Ustalenia:
   - Używamy oznaczenia ZW (zbiór wartości), nie W.
   - Wykluczamy poziome odcinki (żeby nie robić jednoelementowych zbiorów).
   - Wyjaśnienie: kroki + zapis matematyczny.
============================================================= */

function pickClosedFlag() {
  return randInt(0, 1) === 1;
}

function pickY() {
  return randInt(-2, 5);
}

function lerp(xa, ya, xb, yb, x) {
  return ya + ((yb - ya) * (x - xa)) / (xb - xa);
}

function segmentRange(y1, y2, startClosed, endClosed) {
  const increasing = y1 < y2;

  const low = increasing ? y1 : y2;
  const high = increasing ? y2 : y1;

  const lowClosed = increasing ? !!startClosed : !!endClosed;
  const highClosed = increasing ? !!endClosed : !!startClosed;

  return {
    left: low,
    right: high,
    leftClosed: lowClosed,
    rightClosed: highClosed,
  };
}

function normalizeIntervals(intervals) {
  const arr = (intervals || []).filter(Boolean);
  if (!arr.length) return [];

  arr.sort((a, b) => (a.left - b.left) || (a.right - b.right));

  const out = [];
  let cur = { ...arr[0] };

  const canMerge = (x, y) => {
    if (y.left > x.right) return false;
    if (y.left === x.right && !x.rightClosed && !y.leftClosed) return false;
    return true;
  };

  for (let i = 1; i < arr.length; i++) {
    const nxt = arr[i];

    if (!canMerge(cur, nxt)) {
      out.push(cur);
      cur = { ...nxt };
      continue;
    }

    if (nxt.right > cur.right) {
      cur.right = nxt.right;
      cur.rightClosed = !!nxt.rightClosed;
    } else if (nxt.right === cur.right) {
      cur.rightClosed = !!(cur.rightClosed || nxt.rightClosed);
    }

    if (nxt.left === cur.left) {
      cur.leftClosed = !!(cur.leftClosed || nxt.leftClosed);
    }
  }

  out.push(cur);
  return out;
}

/* -------------------- WARIANT A: CIĄGŁA ŁAMANA -------------------- */

function buildDataContinuous() {
  const leftOptions = [-5, -4, -3, -2, -1, 0, 1];

  for (let attempt = 0; attempt < 600; attempt++) {
    const x1 = leftOptions[randInt(0, leftOptions.length - 1)];
    const x3Min = x1 + 4;
    if (x3Min > 5) continue;

    const x3 = randInt(x3Min, 5);
    const x2 = randInt(x1 + 1, x3 - 1);

    const leftClosed = pickClosedFlag();
    const rightClosed = pickClosedFlag();

    const y1 = pickY();
    const y2 = pickY();
    const y3 = pickY();

    // WYKLUCZAMY poziome odcinki:
    if (y1 === y2) continue;
    if (y2 === y3) continue;

    const s1 = (y2 - y1) / (x2 - x1);
    const s2 = (y3 - y2) / (x3 - x2);
    if (Math.abs(s1) > 4 || Math.abs(s2) > 4) continue;

    const eps = 0;

    const points = {
      A: {
        x: x1,
        y: y1,
        markerSymbol: leftClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      Aline: {
        x: x1 + eps,
        y: lerp(x1, y1, x2, y2, x1 + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },

      B: {
        x: x2,
        y: y2,
        showDot: false,
        showLabel: false,
      },
      Bline: {
        x: x2 + eps,
        y: lerp(x2, y2, x3, y3, x2 + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },

      Cline: {
        x: x3 - eps,
        y: lerp(x2, y2, x3, y3, x3 - eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      C: {
        x: x3,
        y: y3,
        markerSymbol: rightClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    };

    const r1 = segmentRange(y1, y2, leftClosed, true);
    const r2 = segmentRange(y2, y3, true, rightClosed);
    const rangeIntervals = normalizeIntervals([r1, r2]);

    return {
      variant: "continuous",
      points,
      curves: [
        sampleAnchorCurve(
          [
            { x: x1, y: y1 },
            { x: x2, y: y2 },
            { x: x3, y: y3 },
          ],
          {
            style: pickCurveStyle(),
            openStart: !leftClosed,
            openEnd: !rightClosed,
          }
        ),
      ],
      rangeIntervals,
      xRange: [-6, 6],
      yRange: [-6, 6],
    };
  }

  return buildDataContinuous();
}

/* -------------------- WARIANT B: ROZŁĄCZNA (SUMA PRZEDZIAŁÓW) -------------------- */

function buildDataDisconnected() {
  const leftStartOptions = [-5, -4, -3, -2, -1, 0, 1];

  for (let attempt = 0; attempt < 900; attempt++) {
    const x1 = leftStartOptions[randInt(0, leftStartOptions.length - 1)];

    const x2Min = x1 + 2;
    if (x2Min > 3) continue;
    const x2 = randInt(x2Min, 3);

    const x3Min = x2 + 1;
    if (x3Min > 4) continue;
    const x3 = randInt(x3Min, 4);

    const x4Min = x3 + 2;
    if (x4Min > 5) continue;
    const x4 = randInt(x4Min, 5);

    const L1 = pickClosedFlag();
    const R1 = pickClosedFlag();
    const L2 = pickClosedFlag();
    const R2 = pickClosedFlag();

    const y1 = pickY();
    const y2 = pickY();
    const y3 = pickY();
    const y4 = pickY();

    // WYKLUCZAMY poziome odcinki:
    if (y1 === y2) continue;
    if (y3 === y4) continue;

    const sL = (y2 - y1) / (x2 - x1);
    const sR = (y4 - y3) / (x4 - x3);
    if (Math.abs(sL) > 4 || Math.abs(sR) > 4) continue;

    const eps = 0;

    const points = {
      A: {
        x: x1,
        y: y1,
        markerSymbol: L1 ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      Aline: {
        x: x1 + eps,
        y: lerp(x1, y1, x2, y2, x1 + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      Bline: {
        x: x2 - eps,
        y: lerp(x1, y1, x2, y2, x2 - eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      B: {
        x: x2,
        y: y2,
        markerSymbol: R1 ? "circle-filled" : "circle-open",
        showLabel: false,
      },

      C: {
        x: x3,
        y: y3,
        markerSymbol: L2 ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      Cline: {
        x: x3 + eps,
        y: lerp(x3, y3, x4, y4, x3 + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      Dline: {
        x: x4 - eps,
        y: lerp(x3, y3, x4, y4, x4 - eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      D: {
        x: x4,
        y: y4,
        markerSymbol: R2 ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    };

    const rL = segmentRange(y1, y2, L1, R1);
    const rR = segmentRange(y3, y4, L2, R2);
    const rangeIntervals = normalizeIntervals([rL, rR]);

    return {
      variant: "disconnected",
      points,
      curves: [
        sampleAnchorCurve(
          [
            { x: x1, y: y1 },
            { x: x2, y: y2 },
          ],
          {
            style: pickCurveStyle(),
            openStart: !L1,
            openEnd: !R1,
          }
        ),
        sampleAnchorCurve(
          [
            { x: x3, y: y3 },
            { x: x4, y: y4 },
          ],
          {
            style: pickCurveStyle(),
            openStart: !L2,
            openEnd: !R2,
          }
        ),
      ],
      rangeIntervals,
      xRange: [-6, 6],
      yRange: [-6, 6],
    };
  }

  return buildDataDisconnected();
}

function buildData() {
  return Math.random() < 0.5 ? buildDataContinuous() : buildDataDisconnected();
}

/* -------------------- API TASKA -------------------- */

export function generateTask() {
  return {
    type: "ReadRangeFromGraphTask",
    data: buildData(),
  };
}

export function renderTask({ data }) {
  const sizePx = 330;

  return (
    <div className="flex flex-col gap-3">
      <p>Na wykresie przedstawiono funkcję. Odczytaj jej zbiór wartości.</p>

      <div style={{ margin: "0 auto", maxWidth: sizePx }}>
        <GraphViewer
          width={sizePx}
          points={data.points}
          curves={data.curves}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>

      <div>
        Zbiór wartości zapisz w postaci{" "}
        <InlineMath math={'y\\in\\_\\_\\_\\_'} />.
      </div>
    </div>
  );
}

/* ---------- INPUT (z podpisem/podglądem jak w answer) ---------- */

export function renderInput(_task, userInput = {}, onChange, disabled) {
  const ui = userInput || {};
  const value = ui.ZW ?? "";

  const handleChange = (val) => {
    onChange({
      ...(userInput || {}),
      ZW: val,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <strong>Zbiór wartości:</strong>
      </div>
      <div><InlineMath math={"y\\in"} /></div>
      <div>
        <IntervalInput
          value={value}
          onChange={handleChange}
          allowUnion={true}
          disabled={disabled}
          previewVariable="y"
        />
      </div>
    </div>
  );
}

/* ---------- CHECK ---------- */

export function checkAnswer(task, userInput = {}) {
  const expected = task?.data?.rangeIntervals;
  if (!Array.isArray(expected) || expected.length === 0) return false;

  const raw = getIntervalRaw(userInput, "ZW");

  if (expected.length === 1) {
    const parsed = parseSingleInterval(raw);
    if (!parsed) return false;

    return intervalsEqualList(
      [
        {
          left: expected[0].left,
          right: expected[0].right,
          leftClosed: !!expected[0].leftClosed,
          rightClosed: !!expected[0].rightClosed,
        },
      ],
      [
        {
          left: parsed.left,
          right: parsed.right,
          leftClosed: !!parsed.leftClosed,
          rightClosed: !!parsed.rightClosed,
        },
      ]
    );
  }

  const parsedUnion = parseIntervalsUnion(raw);
  if (!parsedUnion) return false;

  return intervalsEqualList(expected, parsedUnion);
}

/* ---------- ANSWER ---------- */

export function renderAnswer(task) {
  const expected = task?.data?.rangeIntervals;
  if (!Array.isArray(expected) || expected.length === 0) return null;

  const toLatex = (I) => {
    const l = I.leftClosed ? "[" : "(";
    const r = I.rightClosed ? "]" : ")";
    return `${l}${I.left},\\,${I.right}${r}`;
  };

  if (expected.length === 1) {
    return <InlineMath math={`y\\in ${toLatex(expected[0])}`} />;
  }

  return <InlineMath math={`y\\in ${expected.map(toLatex).join('\\cup')}`} />;
}

/* ---------- EXPLANATION (kroki) ---------- */

export function renderExplanation(task) {
  const expected = task?.data?.rangeIntervals;
  if (!Array.isArray(expected) || expected.length === 0) return null;

  const toLatex = (I) => {
    const l = I.leftClosed ? "[" : "(";
    const r = I.rightClosed ? "]" : ")";
    return `${l}${I.left},\\,${I.right}${r}`;
  };

  const finalLatex =
    expected.length === 1
      ? `y\\in ${toLatex(expected[0])}`
      : `y\\in ${expected.map(toLatex).join('\\cup')}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Zbiór wartości to wszystkie liczby, które pojawiają się na osi <InlineMath math={'y'} /> na wykresie funkcji.</div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Odczytaj z wykresu najmniejszą i największą wartość <InlineMath math={'y'} />.
        Uwzględnij kółka otwarte/domknięte (czy dana wartość należy do zbioru).
      </div>

      {expected.length > 1 ? (
        <div style={{ marginTop: 8 }}>
          <strong>Krok 3.</strong> Jeśli wykres ma rozłączne części, to zbiór wartości jest sumą odpowiednich przedziałów.
        </div>
      ) : null}

      <div style={{ marginTop: 10 }}>
        <InlineMath math={finalLatex} />
      </div>
    </div>
  );
}

/* ---------- USER ANSWER ---------- */

export function renderUserAnswer(userAnswer) {
  const raw = getIntervalRaw(userAnswer, "ZW");
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`y\\in ${raw}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
